import React, { useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { Sparkles, Check, X } from "lucide-react";
import { TIER_VISUALS } from "./VipMembershipCard";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

const BENEFIT_LABELS = (pct, tierLabel) => [
    `${pct}% Lifetime Discount`,
    "Higher VIP Status",
    "Bigger Savings",
    "Exclusive Rewards",
    `${tierLabel} Member Card`,
];

/**
 * Premium one-time level-up modal. Fires a gold confetti burst on mount and
 * auto-marks the celebration as seen on close.
 */
export const VipLevelUpModal = ({ tier, discountPct, onSeen }) => {
    const v = TIER_VISUALS[tier] || TIER_VISUALS.gold;
    const fired = useRef(false);

    useEffect(() => {
        if (fired.current || !tier) return;
        fired.current = true;
        // Tier-tinted confetti palette
        const palette = {
            bronze: ["#CD7F32", "#E0996D", "#FFB378", "#7A3C1A"],
            silver: ["#E8ECF4", "#B8BCC4", "#FFFFFF", "#9DA3AE"],
            gold: ["#E9BC0B", "#FFD700", "#FFE9A6", "#FFAA00"],
            platinum: ["#AADCFF", "#7DC6FF", "#FFFFFF", "#5A9FE0"],
            diamond: ["#C896FF", "#8050FF", "#5080FF", "#FFFFFF"],
        }[tier] || ["#E9BC0B", "#FFD700", "#FFFFFF"];

        const end = Date.now() + 1800;
        const frame = () => {
            confetti({
                particleCount: 4,
                angle: 60,
                spread: 60,
                startVelocity: 55,
                origin: { x: 0, y: 0.7 },
                colors: palette,
                ticks: 200,
            });
            confetti({
                particleCount: 4,
                angle: 120,
                spread: 60,
                startVelocity: 55,
                origin: { x: 1, y: 0.7 },
                colors: palette,
                ticks: 200,
            });
            if (Date.now() < end) requestAnimationFrame(frame);
        };
        requestAnimationFrame(frame);
        // initial burst
        confetti({
            particleCount: 90,
            spread: 100,
            origin: { y: 0.45 },
            colors: palette,
            scalar: 1.1,
            ticks: 300,
        });
    }, [tier]);

    const handleClose = () => {
        if (onSeen) onSeen(tier);
    };

    return (
        <Dialog open={!!tier} onOpenChange={(open) => { if (!open) handleClose(); }}>
            <DialogContent
                className="vip-level-up-modal max-w-md p-0 overflow-hidden border-0 bg-transparent shadow-none"
                data-testid="vip-level-up-modal"
            >
                <DialogTitle className="sr-only">Welcome to {v.label} VIP</DialogTitle>
                <DialogDescription className="sr-only">
                    You have reached the {v.label} VIP tier and unlocked a {discountPct}% lifetime discount.
                </DialogDescription>
                <div
                    className="relative rounded-3xl overflow-hidden p-7 sm:p-8"
                    style={{
                        background: v.bg,
                        border: `1px solid ${v.border}`,
                        boxShadow: v.glow,
                    }}
                >
                    <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: v.pattern }} />
                    <button
                        onClick={handleClose}
                        className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm border border-white/15 flex items-center justify-center hover:bg-black/60"
                        data-testid="vip-level-up-close"
                        aria-label="Close"
                    >
                        <X size={14} className="text-white/80" />
                    </button>

                    <div className="relative z-10 text-center">
                        <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full mb-4"
                            style={{ background: `${v.accentSoft}`, border: `1px solid ${v.border}` }}>
                            <Sparkles size={12} style={{ color: v.accent }} />
                            <span className="text-[10px] font-mono uppercase tracking-[0.3em] font-bold" style={{ color: v.accent }}>
                                Tier Unlocked
                            </span>
                        </div>

                        <h2 className="font-display text-3xl sm:text-4xl font-black text-white mb-1">
                            🎉 Congratulations!
                        </h2>
                        <p className="text-base sm:text-lg text-white/70 mb-5">
                            Welcome to{" "}
                            <span className="font-display font-bold" style={{ color: v.accent }}>
                                {v.label} VIP
                            </span>
                        </p>

                        <div className="text-left bg-black/30 backdrop-blur-sm rounded-2xl p-4 sm:p-5 mb-5 border border-white/10">
                            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mb-3">
                                You have unlocked
                            </p>
                            <ul className="space-y-2">
                                {BENEFIT_LABELS(discountPct, v.label).map((b, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm text-white">
                                        <span
                                            className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                                            style={{
                                                background: v.accentSoft,
                                                border: `1px solid ${v.border}`,
                                            }}
                                        >
                                            <Check size={11} style={{ color: v.accent }} strokeWidth={3} />
                                        </span>
                                        <span data-testid={`vip-level-up-benefit-${i}`}>{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <button
                            onClick={handleClose}
                            className="w-full py-3 rounded-full font-mono uppercase tracking-[0.25em] text-sm font-bold transition-all hover:scale-[1.02]"
                            style={{
                                background: `linear-gradient(135deg, ${v.accent}, ${v.accentSoft})`,
                                color: "#0a0905",
                                boxShadow: `0 8px 24px ${v.accentSoft}`,
                            }}
                            data-testid="vip-level-up-claim"
                        >
                            Claim & Continue
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
